import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import Paper from "@mui/material/Paper";
import { ThemeProvider } from "@mui/material/styles";
import StyledEngineProvider from "@mui/styled-engine/StyledEngineProvider";
import React, { useEffect } from "react";
import { createRoot } from "react-dom/client";
import { useTranslation } from "react-i18next";
import I18nRoot from "../modules/common/i18n/I18nRoot";
import materialTheme from "../theme";

function BaseSearch(props: {
  onSubmit?: string | (() => void);
  variant?: "elevation" | "outlined";
  elId: string;
  placeholder: string;
}) {
  const { t } = useTranslation("common");
  const [search, setSearch] = React.useState("");

  const submitSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (props.onSubmit) {
      if (typeof props.onSubmit === "function") {
        props.onSubmit();
      } else if (typeof props.onSubmit === "string") {
        // @ts-expect-error yes, this is a bit hacky
        (window.parent[props.onSubmit] as () => void)();
      }
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  useEffect(() => {
    document.addEventListener("reset-search-input", () => {
      setSearch("");
    });
  }, []);

  return (
    <Paper sx={{ boxShadow: "none" }} variant={props.variant} data-test-id="base-search-content">
      <Box
        component="form"
        onSubmit={submitSearch}
        sx={{
          display: "flex",
          width: "100%",
          alignItems: "center",
          justifyContent: "space-between",
        }}
        data-test-id="base-search-form"
      >
        <InputBase
          id={props.elId}
          data-test-id="base-search-input"
          placeholder={props.placeholder || t("search.placeholder")}
          slotProps={{
            input: { "aria-label": props.placeholder || t("actions.search") },
          }}
          value={search}
          onChange={handleChange}
          sx={{ pl: 1, flexGrow: 1 }}
        />
        <IconButton
          id={`${props.elId}-submit`}
          type="submit"
          aria-label={t("actions.search")}
          data-test-id="base-search-submit"
        >
          <SearchIcon />
        </IconButton>
      </Box>
    </Paper>
  );
}

/** I18nRoot fallback: a disabled lookalike so the search bar's footprint doesn't shift once it mounts. */
function BaseSearchFallback({ placeholder, variant }: { placeholder: string; variant?: "elevation" | "outlined" }) {
  return (
    <Paper sx={{ boxShadow: "none" }} variant={variant} data-test-id="base-search-content">
      <Box
        sx={{
          display: "flex",
          width: "100%",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <InputBase placeholder={placeholder} disabled sx={{ pl: 1, flexGrow: 1 }} />
        <IconButton disabled>
          <SearchIcon />
        </IconButton>
      </Box>
    </Paper>
  );
}

/*
 * This is necessary because as of MUI v5 useStyles cannot be used in the same
 * component as the root MuiThemeProvider
 */
export default function WrappedBaseSearch(props: React.ComponentProps<typeof BaseSearch>) {
  return (
    <StyledEngineProvider injectFirst enableCssLayer>
      <ThemeProvider theme={materialTheme}>
        <I18nRoot
          namespaces={["common"]}
          fallback={<BaseSearchFallback placeholder={props.placeholder} variant={props.variant} />}
        >
          <BaseSearch {...props} />
        </I18nRoot>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

function renderElements() {
  document.addEventListener("DOMContentLoaded", () => {
    const domContainers = document.getElementsByClassName("base-search");
    if (domContainers.length) {
      [].map.call(domContainers, (container) => {
        const htmlContainer = container as HTMLElement;
        const { dataset } = htmlContainer;
        const variant = dataset.variant === "outlined" || dataset.variant === "elevation" ? dataset.variant : undefined;
        const root = createRoot(container);
        root.render(
          <WrappedBaseSearch
            placeholder={dataset.placeholder ?? ""}
            onSubmit={dataset.onsubmit}
            elId={dataset.elid ?? "base-search"}
            variant={variant}
          />,
        );
      });
    }
  });
}

renderElements();
